<script setup>
import { ref, computed, watch } from 'vue';
import { router, useForm, usePage, Link, Deferred } from '@inertiajs/vue3';
import QRcode from 'qrcode';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
import { BookmarkIcon, ClockIcon, ChevronUpIcon, PencilIcon, CheckIcon, XMarkIcon, EllipsisHorizontalIcon, ClipboardDocumentListIcon, DocumentIcon, Square2StackIcon, QrCodeIcon, TrashIcon, GiftIcon } from '@heroicons/vue/24/outline';
import { StarScale, PopperTooltip, BackdropModal } from '@goodvibes/kudoslink';
import TertiaryButton from '@/Components/TertiaryButton.vue';
import TextAreaInput from '@/Components/TextAreaInput.vue';
import ShareIcon from '@/Components/Icons/Share.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import CheckboxInput from '@/Components/CheckboxInput.vue';
import { Dropdown as FloatingDropdown } from 'floating-vue';
import { can as canDuplicate, exportChatToClipboard, exportChatToPdf } from '@/shared';
import { useModal } from '@/composables';
import FloatingDropdownMenu from './FloatingDropdownMenu.vue';
import TextSkeleton from './Skeletons/Text.vue';

const props = defineProps({
    simulation: { type: Object, required: true },
    humanReadableScenarioDescription: { type: String, required: false },
    previousRuns: { type: Array, required: true },
    playlists: { type: Array, required: false },
});

const page = usePage();
const authUser = computed(() => page.props.auth.user);
const isPartner = computed(() => authUser.value && authUser.value.master.customer_type === 'partner');

const playlistForm = useForm({
    name: '',
});

const isCreateNewPlaylistOpen = ref(false);

function createNewPlaylist() {
    playlistForm.post(route('playlists.store'), {
        onFinish: () => isCreateNewPlaylistOpen.value = false,
    });
}

function isRoleplayPartOfPlaylist(playlist) {   
    return playlist.roleplays.some((element) => element.id == props.simulation.id);
}

function updatePlaylist(playlist) {
    if (!isRoleplayPartOfPlaylist(playlist)){
        router.post(route('playlists.attach', { playlist: playlist.id, roleplay: props.simulation.id}));
    } else {
        router.post(route('playlists.detach', { playlist: playlist.id, roleplay: props.simulation.id}));
    }
}

const playlistModal = useModal(false);

function closePlaylistModal() {
    isCreateNewPlaylistOpen.value = false;
    playlistModal.close()
}

const qrModal = useModal(false);

const qrCode = ref('');
const roleplayLink = computed(() => route('simulations.show', { roleplayUuid: props.simulation.uuid }));

watch(
    roleplayLink,
    async function generateQR() {
        const dataUrl = await QRcode.toDataURL(roleplayLink.value, { width: 300, margin: 0 });
        qrCode.value = dataUrl;
    },
    { immediate: true },
);

// Partner coupon QR code
const partnerCouponModal = useModal(false);
const availableCoupons = ref([]);
const selectedCoupon = ref(null);
const partnerQrCode = ref('');
const partnerRoleplayLink = computed(() => {
    if (!selectedCoupon.value) return '';
    return `${route('simulations.show', { roleplayUuid: props.simulation.uuid })}?coupon=${selectedCoupon.value.code}`;
});

watch(
    partnerRoleplayLink,
    async function generatePartnerQR() {
        if (!partnerRoleplayLink.value) return;
        const dataUrl = await QRcode.toDataURL(partnerRoleplayLink.value, { width: 300, margin: 0 });
        partnerQrCode.value = dataUrl;
    }
);

async function openPartnerCouponModal() {
    // Fetch partner's non-expired coupons
    try {
        const response = await fetch(route('coupons.available'), {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-CSRF-Token': usePage().props.csrf_token,
            },
            credentials: 'same-origin',
        });

        if (!response.ok) {
            throw new Error(response.statusText);
        }

        const data = await response.json();
        availableCoupons.value = data;
        selectedCoupon.value = null;
        partnerCouponModal.open();
    } catch (error) {
        console.error('Error fetching coupons:', error);
    }
}

function selectCoupon(coupon) {
    selectedCoupon.value = coupon;
}

function copyPartnerRoleplayLinkToClipboard() {
    if (partnerRoleplayLink.value) {
        navigator.clipboard.writeText(partnerRoleplayLink.value);
    }
}

const editScenario = ref(false);

const scenarioDescriptionForm = useForm({
    scenario: {
        description_human_readable: props.simulation.scenario.description_human_readable.original,
    }
});

function updateScenario() {
    scenarioDescriptionForm.post(route('simulations.update', { maybeSharableSimulation: props.simulation.id }), {
        onSuccess: () => {
            editScenario.value = false;
        },
    });
}

function copyRoleplayLinkToClipboard() {
    navigator.clipboard.writeText(route('simulations.show', { roleplayUuid: props.simulation.uuid }));
}

async function fetchSimulationRun(simulationRunUuid) {
    const response = await fetch(route('simulationRuns.show', { threadId: simulationRunUuid }), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': usePage().props.csrf_token,
        },
        credentials: 'same-origin',
    });

    if (!response.ok) {
        throw new Error(response.statusText);
    }

    const simulationRun = await response.json();

    return simulationRun;
}

async function handleClipboardExport(simulationRun) {
    const roleplayRun = await fetchSimulationRun(simulationRun.uuid);
    exportChatToClipboard(roleplayRun.messages)
}

async function handlePdfExport(simulationRun) {
    const roleplayRun = await fetchSimulationRun(simulationRun.uuid);
    exportChatToPdf(roleplayRun.messages, { title: roleplayRun.simulation.name, score: roleplayRun.points })
}

function isCurrentRoleplayRun(roleplayRun) {
    const currentThreadId = route().params.threadId;
    return roleplayRun.thread_id === currentThreadId;
}

const canEditRoleplay = computed(() => {
   return props.simulation?.editors?.find((editor) => editor.id == authUser.value.id) ? true : false;
});
</script>

<template>
    <div class="flex flex-col h-full max-h-[calc(100dvh-2rem)] bg-slate-100">
        <div class="shrink">
            <div class="flex items-center gap-x-4">
                <img :src="simulation.thumbnail" class="w-[6em] aspect-square rounded-full object-cover shrink-0 grow-0" />
                <div class="grow">
                    <p class="font-bold">{{ simulation.name }}</p>
                    <Link :href="route('company.show', { companyId: simulation.author.master_id })" class="mt-[0.25em] text-muted text-xs font-semibold hover:text-slate-500">
                        by @{{ simulation.author.companyName ? simulation.author.companyName : simulation.author.name }}
                    </Link>
                    <div v-if="simulation.isReviewable" class="flex items-center text-muted">
                        <span class="text-xs">Your score: </span>
                        <StarScale class="inline-block scale-50 -ml-6" :disabled="true" :scale="3"
                            :answer="simulation.highestPointOnSimulationByUser ? simulation.highestPointOnSimulationByUser : 0" />
                    </div>
                </div>
            </div>

            <div class="flex flex-col flex-wrap md:flex-nowrap gap-2 mt-2">
                <div class="flex gap-x-3">
                    <slot name="actions"></slot>
                </div>
                <div class="flex gap-x-2 mt-2">
                    <FloatingDropdownMenu placement="left" class="z-10" :popperHideTriggers="['click']">
                        <template #trigger>
                            <PopperTooltip @click="copyRoleplayLinkToClipboard" placement="bottom" :triggers="['hover']" :autoHide="true">
                                <ShareIcon class="cursor-pointer h-5 w-5 px-4 py-2  bg-white rounded-full box-content" />
                                <template #popper>
                                    Share roleplay link
                                </template>
                            </PopperTooltip>
                        </template>
                        
                        <li>
                            <PopperTooltip placement="right" :triggers="['click']" :hideTriggers="['hover', 'focus']" :delay="{ hide: 1000 }" :autoHide="true">
                                <button v-close-popper @click="copyRoleplayLinkToClipboard" class="py-1 block">
                                    <ClipboardDocumentListIcon class="h-5 inline-block" />
                                    <span class="ml-1">Copy to clipboard</span>
                                </button>
                                <template #popper>
                                    Roleplay link copied!
                                </template>
                            </PopperTooltip>
                        </li>
                        <li>
                            <button v-close-popper @click="qrModal.open()" class="py-1 block">
                                <QrCodeIcon class="h-5 inline-block" />
                                <span class="ml-1">Show QR code</span>
                            </button>
                        </li>
                        <li>
                            <a :href="qrCode" class="block" :download="`${simulation.name}-QR.png`">
                                <QrCodeIcon class="h-5 inline-block" />
                                <span class="ml-1">Download QR code</span>
                            </a>
                        </li>
                        <li v-if="isPartner">
                            <button v-close-popper @click="openPartnerCouponModal" class="py-1 block">
                                <GiftIcon class="h-5 inline-block" />
                                <span class="ml-1">Create special coupon QR code</span>
                            </button>
                        </li>
                    </FloatingDropdownMenu>
                    
                    <PopperTooltip placement="bottom" v-if="authUser && !authUser.isDemoUser">
                        <button 
                            @click="playlistModal.open()"
                            class="text-slate-700 hover:bg-slate-300 rounded-3xl text-sm font-bold px-4 py-2 whitespace-nowrap bg-white"
                        >
                            <BookmarkIcon class="h-5 text-gray-600" />
                        </button>
                        <template #popper>
                            <p>{{ 'Add to any playlist' }}</p>
                        </template>
                    </PopperTooltip>
                    <FloatingDropdownMenu v-if="simulation.author.id === authUser?.id || canEditRoleplay || canDuplicate(authUser, 'duplicate', simulation)" :distance="6" placement="right">
                        <template #trigger>
                            <EllipsisHorizontalIcon class="cursor-pointer h-5 w-5 px-4 py-2 stroke-current bg-white rounded-full box-content" />
                        </template>
                        
                        <li v-if="simulation.author.id === authUser?.id || canEditRoleplay">
                            <Link class="py-1" :href="route('simulations.edit', { maybeSharableSimulation: simulation.id })">
                                <PencilIcon class="h-5 inline-block" /> 
                                <span class="ml-1">Edit</span>
                            </Link>
                        </li>
                        <li v-if="canDuplicate(authUser, 'duplicate', simulation)">
                            <Link class="py-1" :href="route('simulations.duplicate', { simulationId: simulation.id })">
                                <Square2StackIcon class="h-5 inline-block" /> 
                                <span class="ml-1">Duplicate</span>
                            </Link>
                        </li>
                    </FloatingDropdownMenu>
                </div>
            </div>
        </div>

        <div class="w-full mt-8 overflow-y-auto">
            <Disclosure v-slot="{ open }" as="div" :defaultOpen="true">
                <DisclosureButton
                    class="flex w-full gap-x-4 justify-between rounded-2xl px-4 py-2 text-left text-sm font-medium text-slate-700 focus:outline-none focus-visible:ring focus-visible:ring-primary/75"
                >
                    <BookmarkIcon class="h-5 text-current" />
                    <div class="grow">
                        <span class=" inline-block align-middle">Scenario</span>
                        <PencilIcon 
                            class="h-4 text-current inline-block align-middle ml-2" v-show="authUser?.id === simulation.author.id || canEditRoleplay"
                            @click="editScenario = true" 
                        />
                    </div>
                </DisclosureButton>
                <transition
                    enter-active-class="transition duration-100 ease-in"
                    enter-from-class="transform scale-95 opacity-0"
                    enter-to-class="transform scale-100 opacity-100"
                    leave-active-class="transition duration-75 ease-out"
                    leave-from-class="transform scale-100 opacity-100"
                    leave-to-class="transform scale-95 opacity-0"
                >
                    <DisclosurePanel static class="px-4 pb-2 pt-4 text-sm text-gray-500">
                        <template v-if="editScenario">
                            <TextAreaInput v-model="scenarioDescriptionForm.scenario.description_human_readable" inputClass="h-96 overflow-y-auto resize-none" />
                            <div class="flex gap-x-2 justify-end mt-1">
                                <TertiaryButton v-if="!scenarioDescriptionForm.processing" @click="editScenario = false"><XMarkIcon class="h-5 text-current" /></TertiaryButton>
                                <PrimaryButton :disabled="scenarioDescriptionForm.processing" @click="updateScenario">
                                    <Spinner v-if="scenarioDescriptionForm.processing" class="animate-spin text-current h-3" />
                                    <CheckIcon v-else class="h-5 text-current" />
                                </PrimaryButton>
                            </div>
                        </template>
                        <Deferred v-else data="humanReadableScenarioDescription">
                            <template #fallback>
                                <TextSkeleton />
                            </template>
                            <p  class="leading-relaxed text-justify">{{ humanReadableScenarioDescription }}</p>
                        </Deferred>
                    </DisclosurePanel>
                </transition>
            </Disclosure>
            <Disclosure v-if="authUser && !authUser.isDemoUser" as="div" v-slot="{ open }" :defaultOpen="true" class="mt-2">
                <DisclosureButton
                    class="flex w-full gap-x-4 justify-between rounded-2xl px-4 py-2 text-left text-sm font-medium text-slate-700 focus:outline-none focus-visible:ring focus-visible:ring-primary/75"
                >
                    <ClockIcon class="h-5 text-current" />
                    <span class="grow">History</span>
                    <ChevronUpIcon
                        :class="open ? 'rotate-180 transform' : ''"
                        class="h-5 w-5 text-current transition"
                    />
                </DisclosureButton>
                <transition
                    enter-active-class="transition duration-100 ease-in"
                    enter-from-class="transform scale-95 opacity-0"
                    enter-to-class="transform scale-100 opacity-100"
                    leave-active-class="transition duration-75 ease-out"
                    leave-from-class="transform scale-100 opacity-100"
                    leave-to-class="transform scale-95 opacity-0"
                >
                    <DisclosurePanel class="px-4 pb-2 pt-4 text-gray-500">
                        <ul v-if="previousRuns.length" class="space-y-2">
                            <li v-for="prevSimulationRun in previousRuns" :key="prevSimulationRun.id" 
                                class="text-xs px-4 py-2 rounded-full flex gap-x-2 items-center justify-between"
                                :class="[isCurrentRoleplayRun(prevSimulationRun) ? 'bg-primary/10 text-primary font-semibold' : 'bg-white']"
                            >
                                <Link 
                                    :href="route('simulationRuns.show', { threadId: prevSimulationRun.thread_id })"
                                    class="inline-flex gap-x-2 items-center w-full"
                                >
                                    <span>{{ (new Date(prevSimulationRun.created_at)).toLocaleDateString() }}</span>
                                    <StarScale 
                                        v-if="simulation.isReviewable"
                                        class="inline-block scale-50 w-24" 
                                        :disabled="true" 
                                        :scale="3"
                                        :answer="prevSimulationRun.points ? prevSimulationRun.points : 0" 
                                    />   
                                </Link>
                                <FloatingDropdown :distance="6" placement="right">
                                    <EllipsisHorizontalIcon class="cursor-pointer h-6 stroke-current box-content" />

                                    <template #popper>
                                        <ul class="bg-white px-4 py-3 space-y-3 text-sm">
                                            <li>
                                                <PopperTooltip placement="bottom" :triggers="['click']" :hideTriggers="['hover']" :delay="{ hide: 500 }" :autoHide="true">
                                                    <button class="py-1" @click="handleClipboardExport(prevSimulationRun)">
                                                        <ClipboardDocumentListIcon class="h-5 inline-block" /> 
                                                        <span class="ml-1">Export chat to clipboard</span>
                                                    </button>
                                                    <template #popper>
                                                        Chat copied to clipboard!
                                                    </template>
                                                </PopperTooltip>
                                            </li>
                                            <li>
                                                <PopperTooltip placement="bottom" :triggers="['click']" :hideTriggers="['hover']" :delay="{ hide: 500 }" :autoHide="true">
                                                    <button class="py-1" @click="handlePdfExport(prevSimulationRun)">
                                                        <DocumentIcon class="h-5 inline-block" /> 
                                                        <span class="ml-1">Export chat to pdf</span>
                                                    </button>
                                                    <template #popper>
                                                        Chat exported to pdf!
                                                    </template>
                                                </PopperTooltip>
                                            </li>
                                            <li>
                                                <PopperTooltip placement="bottom" :triggers="['click']" :hideTriggers="['hover']" :delay="{ hide: 500 }" :autoHide="true">
                                                    <button class="py-1" @click="router.delete(route('simulationRuns.destroy', { simulationRun: prevSimulationRun.id }))">
                                                        <TrashIcon class="h-5 inline-block" /> 
                                                        <span class="ml-1">Remove</span>
                                                    </button>
                                                    <template #popper>
                                                        Delete this simulation run for eternity!
                                                    </template>
                                                </PopperTooltip>
                                            </li>
                                        </ul>
                                    </template>
                                </FloatingDropdown>
                            </li>
                        </ul>
                        <p v-else class="text-center text-sm text-muted">You don't have any previous conversations yet.</p>
                    </DisclosurePanel>
                </transition>
            </Disclosure> 
        </div>
    </div>

    <BackdropModal class="overflow-scroll" :show="playlistModal.isOpen" @close="closePlaylistModal">
        <div class="bg-white mx-auto px-4 py-10 md:px-6 rounded-[2.5rem] w-full max-w-xl flex flex-col items-stretch gap-y-6">
            <div v-if="!isCreateNewPlaylistOpen" class="mx-auto">
                <div class="text-2xl text-center">Save roleplay to...</div>
                <div v-for="playlist in playlists" class="flex items-center mt-2 gap-x-3">
                    <CheckboxInput
                            :modelValue="isRoleplayPartOfPlaylist(playlist)"
                            @update:modelValue="updatePlaylist(playlist)"
                            class="shrink-0 ml-5"
                        >
                        </CheckboxInput>
                    {{playlist.name}}
                </div>
                <div class="flex gap-x-3">
                    <SecondaryButton @click="isCreateNewPlaylistOpen = true" class="mt-5">Create new playlist</SecondaryButton>
                    <PrimaryButton @click="closePlaylistModal" class="mt-5">Save</PrimaryButton>
                </div>
            </div>
            <div v-else>
                <div class="text-2xl text-center gap-3 mb-5">Create new playlist</div>
                <TextInput v-model="playlistForm.name" :errors="playlistForm.errors.name && [playlistForm.errors.name]" placeholder="Name" />
                <div class="flex gap-x-3 mt-5 justify-center">
                    <PrimaryButton @click="createNewPlaylist">
                        Create
                    </PrimaryButton>
                    <SecondaryButton @click="isCreateNewPlaylistOpen = false">Cancel</SecondaryButton>
                </div>
            </div>
        </div>
    </BackdropModal>
    <BackdropModal class="overflow-scroll !z-[1001]" :show="qrModal.isOpen" @close="qrModal.close()">
        <div class="bg-white mx-auto px-4 py-10 md:px-6 rounded-[2.5rem] w-full max-w-xl flex flex-col items-stretch gap-y-6">
            <div class="text-xl text-center">{{ simulation.name }} QR code</div>
            <div class="mx-auto w-[300px] aspect-square">
                <img :src="qrCode" class="w-full" alt="QR code for roleplay" />
            </div>
            <div class="text-sm mt-3 text-center">Scan to join</div>
        </div>
    </BackdropModal>

    <!-- Partner Special Coupon QR Code Modal -->
    <BackdropModal class="overflow-scroll !z-[1002]" :show="partnerCouponModal.isOpen" @close="partnerCouponModal.close()">
        <div class="bg-white mx-auto px-4 py-10 md:px-6 rounded-[2.5rem] w-full max-w-xl flex flex-col items-stretch gap-y-6">
            <div class="text-xl text-center mb-4">Create special coupon QR code</div>
            
            <div v-if="!selectedCoupon">
                <p class="text-sm text-center mb-4">Select one of your available coupons to create a special QR code:</p>
                
                <div v-if="availableCoupons.length === 0" class="text-center text-sm text-muted py-4">
                    You don't have any non-expired coupons available.
                </div>
                
                <div v-else class="space-y-3 max-h-[300px] overflow-y-auto">
                    <div 
                        v-for="coupon in availableCoupons" 
                        :key="coupon.id" 
                        @click="selectCoupon(coupon)"
                        class="p-3 rounded-lg bg-slate-100 cursor-pointer hover:bg-slate-200 transition-colors"
                    >
                        <div class="flex justify-between">
                            <div class="font-medium">{{ coupon.code }}</div>
                            <div class="font-bold text-primary">{{ coupon.credits }} credits</div>
                        </div>
                        <div class="text-xs text-muted mt-1">
                            <span v-if="coupon.redeemable_until">Expires: {{ new Date(coupon.redeemable_until).toLocaleDateString() }}</span>
                            <span v-else>Never expires</span>
                            <span v-if="coupon.redeemable_count" class="ml-2">
                                Used: {{ coupon.redeemed_count }}/{{ coupon.redeemable_count }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            
            <div v-else>
                <div class="text-center mb-4">
                    <div class="text-lg font-medium">Coupon: {{ selectedCoupon.code }}</div>
                    <div class="text-sm text-primary font-bold">{{ selectedCoupon.credits }} credits</div>
                </div>
                
                <div class="mx-auto w-[300px] aspect-square">
                    <img :src="partnerQrCode" class="w-full" alt="Special QR code with coupon" />
                </div>
                
                <div class="flex justify-center mt-4">
                    <PopperTooltip placement="top" :triggers="['click']" :hideTriggers="['hover']" :delay="{ hide: 1000 }" :autoHide="true">
                        <button @click="copyPartnerRoleplayLinkToClipboard" class="flex items-center gap-x-2 px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors">
                            <ClipboardDocumentListIcon class="h-5" />
                            <span>Copy link to clipboard</span>
                        </button>
                        <template #popper>
                            Special link copied!
                        </template>
                    </PopperTooltip>
                </div>
                
                <div class="flex justify-center mt-4">
                    <SecondaryButton @click="selectedCoupon = null">
                        Back to coupon selection
                    </SecondaryButton>
                </div>
            </div>
        </div>
    </BackdropModal>
</template>